import { Component, ViewEncapsulation, OnInit, AfterViewInit, ViewChild, Input, ChangeDetectorRef, OnDestroy } from "@angular/core";
import { FormControl, FormGroup, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { fadeInOut } from "../../../app/shared/helpers/animations";
import { DialogComponent, TooltipComponent } from "@syncfusion/ej2-angular-popups";
import { EmitType } from '@syncfusion/ej2-base';
import { IApiTypedResult, IBusinessOperationQualifier, ILLGenericKeys, ITermByIndustryUsageAndCategory, Order, OrderUser, SplFileStorageResult, User } from "../../../app/shared/models";
import { AuthorizationService, LLFormService, OrderService, SettingsService, StorageManagerService } from "../../../app/shared/services";
import { ActivatedRoute, Router } from "@angular/router";
import { ActionCompleteEventArgs, ChangeEventArgs, SelectedEventArgs, UploaderComponent, UploadingEventArgs } from "@syncfusion/ej2-angular-inputs";
import { ChangeArgs } from "@syncfusion/ej2-angular-buttons";
import { ToastrService } from "ngx-toastr";
import { Media } from "../../shared/models/media.model";
import { GridComponent, PageSettingsModel, RowDeselectEventArgs, RowSelectEventArgs } from "@syncfusion/ej2-angular-grids";
import { Utilities } from "../../shared/services/utilities";
import { FeatureService } from '../../shared/services/feature.service';
import { catchError } from "rxjs";

@Component({
    selector: 'app-order',
    templateUrl: './order.component.html',
    styleUrls: ['./order.component.scss'],
    encapsulation: ViewEncapsulation.Emulated,
    animations: [fadeInOut],
    standalone: false
})
export class OrderComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('confirmDialog') public confirmDialog: DialogComponent;
    @ViewChild('llFormGrid') public llFormGrid: GridComponent;
    // Baseline SPL grid
    @ViewChild('baselineSplGrid') public baselineSplGrid: GridComponent;
    @Input() baselineSplGridData: any[];

    @ViewChild('tooltipDC')
    public tooltipDC: TooltipComponent;

    @ViewChild('tooltipSPL')
    public tooltipSPL: TooltipComponent;

    order: Order;
    orderForm: UntypedFormGroup;
    orderRevisionId: number;
    animationSettings: Object = { effect: 'Zoom' };
    position: object = { X: 'center', Y: 'center' };
    ddwFields: Object = { text: 'label', value: 'code' };
    ddwAdminFields: Object = { text: 'termValueDescription', value: 'termValueCode' };

    serviceCategoryToServiceTypeAssoc: IBusinessOperationQualifier[];
    serviceTypesLOV: ILLGenericKeys[];
    serviceGroupsLOV: String[];
    countryLOV: String[] = ['United States','Canada'];
    freezeChangeEventsTemporarily: boolean;
    canEdit: boolean = false;
    canOrderAdmin: boolean = false;
    // List of all employees in company
    allEmployeesInCompany: User[] = null;

    inactiveOrderEmailRecipientList: any[] = [];
    inactiveOrderOwnersList: any[] = [];
    // Hold on to original values for these fields for saving later
    beforeOrderStatus: string;
    beforeConfirmedDate: Date;

    // Source docs
    sourceDocs: Media[] = null;

    llFormGridData: any[];

    // ------------------------------------------------------------
    // orderOwners multi-select pulldown
    // ------------------------------------------------------------
    // Map keys/values for owners pulldown list
    orderOwnersFields = { 'text': 'label', 'value': 'code' };
    // Placeholder when order owners pulldown empty
    orderOwnersWaterMark = 'Select Owners';
    // Will hold possible values for list
    orderOwnersLOV: ILLGenericKeys[];
    // Can user request cancellation?
    canRequestCancellation: boolean;

    // Reference data list
    serviceTypeToDeliveryPriorityAssoc: IBusinessOperationQualifier[];

    orderStatuses: ITermByIndustryUsageAndCategory[] = null;

    public showIngestFile: boolean = false;
    // Delivery doc associated with this order
    // (not the source docs)
    deliveryDoc: Media = null;

    // If user deletes an existing delivery doc,
    // there's no angular form changes, but we need to force
    // a page reload.
    deliveryDocStatusChange: boolean;

    // Save button caption
    saveButtonCaption = 'Save as Draft';
    countryServiceGroup: IBusinessOperationQualifier[];
    countryServiceType: IBusinessOperationQualifier[];



    // ------------------------------------------------------------
    // Post save settings
    // ------------------------------------------------------------
    // Save the order object, when finished then optionally finish a deliverDoc
    // upload.  There's a lot of async steps, so put information about
    // saving orders and docs into class variables that can be set
    // in saveData and accessed in postSaveData to tie everything together.

    // Message from server about saving the order
    postSaveDataMessage: string;
    // URL to go to after save is finished.  Note we need the
    // new OrderRevisionId of the newly saved order revision
    // to generate this URL (usually).
    postSaveDataRedirectUrl: string;
    // OrderRevisionId from after we save order changes (but we haven't uploaded files yet so we need this stored)
    postSaveDataNewOrderRevisionId: number;
    // Track if source docs are finished uploading after order save
    postSaveWaitingForSourceDocUpload: boolean;
    // Track if delivery docs are finished uploading after order save
    postSaveWaitingForDeliveryUpload: boolean;

    // Should serviceType pulldown be enabled?
    isServiceTypeEnabled = false;
    // Should deliveryPriority radio buttons be enabled?
    isDeliveryPriorityEnabled = false;
    ingestFileValue: boolean = true;

    // Will hold possible values for list
    orderRecipientsLOV: ILLGenericKeys[];
    isServiceGroupSelected: boolean = false;
    isServiceCategorySelected: boolean = false;
    // Regular priority box
    hasRegularPriority = true;

    // Expedited priority box
    hasExpeditedPriority = true;

    // Emergency priority box
    hasEmergencyPriority = true;

    // Order type
    showOrderType = false;
    isServiceTypeSelected: boolean;
    pageState: string;
    // Summary table for selected LL form grid row
    llFormGridSelectedRowData: any = null;
    showLlForm = true;
    // Should we show description box
    showOrderDescription = true;
    llFormInitialPage: PageSettingsModel;
    llFormSortOptions: Object;
    llFormToolbarOptions: Object;
    llFormRecordCount: number;
    llFormGridSelectionOptions: object;
    gridFormatOptions: object;

    fileMinSize: number;
    fileMaxSize: number;

    baselineSplInitialPage: PageSettingsModel;
    baselineSplSortOptions: Object;
    baselineSplToolbarOptions: Object;
    baselineSplRecordCount: number;
    baselineSplLoading = true;
    baselineSplSearchForm: UntypedFormGroup;
    baselineSplSelectionOptions: object;
    // ID of record currently selected in grid
    baselineSplGridSelectedId: number;
    // When loading an order, first the llFormGrid gets populated,
    // then usually a row gets selected, then that triggers
    // baselineSplGrid to call an API to get data, then
    // bind that data to the grid.  Only after that can
    // we select the correct row of the baselineSplGrid
    // based on what's in the order object.
    // That's outside the normal ngInit and load process,
    // so set a flag and each time the baselineSplGrid
    // load is finished, check to see if it's the first time,
    // if so select the correct row.
    wasFirstTimeBaselineSplGridSelectionDone: boolean;
    bIsSelectedLLFormActive = true;

    // Error message to display on bottom of screen.
    errorMessage = '';

    // Tells uploader what URL to post files to (gets set programmatically later)
    adminFileUploadAsyncSettings: any = null;

    // ------------------------------------------------------------
    // File upload
    // ------------------------------------------------------------

    // Angular object
    @ViewChild('fileUpload')
    public fileUpload: UploaderComponent;

    // Angular object
    @ViewChild('adminFileUpload')
    public adminFileUpload: UploaderComponent;

    // Tells uploader what URL to post files to (gets set programmatically later)
    fileUploadAsyncSettings: any = null;
    // Pointer to drop element
    dropElement: HTMLElement = null;
    // Valid extensions to upload
    validFileExtensionsStr: string;
    showTextboxesForChanges: boolean = true;
    isScsIngestFileFeatureVisible: boolean = false;

    orderRecipientsFields = { 'text': 'label', 'value': 'code' };

    // If user clicks "delete" for existing
    // delivery doc
    requestDeletionOfExistingDeliveryDoc: boolean;

    public clipmodecolumn = { class: 'e-tooltipcolumn' };

    // Valid extensions to upload.  In this case only zip files.
    adminValidFileExtensionsStr = '.zip';
    adminFileMinSize: number;
    adminFileMaxSize: number;

    dateFormat: any;
    orderStatusesNoDraft: ITermByIndustryUsageAndCategory[];
    canSubmitOrder: boolean;
    validForm: boolean;

    canModifyOrder: boolean = true;

    public dropEle?: HTMLElement;
    countrySelected = 'United States';
    isNewOrder: any;
    hasFailedFiles: any;
    processedFilesCount: any = 0;
    metaDataXmlFilePath: string;
    metaDataXmlFileName: string;

    constructor(
        private fb: UntypedFormBuilder,
        private authorizationService: AuthorizationService,
        private activatedRoute: ActivatedRoute,
        private storageManagerService: StorageManagerService,
        private router: Router,
        private toastr: ToastrService,
        private cdRef: ChangeDetectorRef,
        private llService: LLFormService,
        private utilService: Utilities,
        private settingsService: SettingsService,
        private featureService: FeatureService,
        public orderService: OrderService,
    ) {
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
     
            // Get data from the resolver and populate various dropdownlists
            this.loadData();

            this.checkOrderType();
            this.populateServiceGroupLOV();
            this.populateEmployeeLists();
            this.getForm('admin', 'confirmedDueDate').markAsPristine();

            this.adminFileUploadAsyncSettings = {
                saveUrl: this.orderService.getFileUploadUrl()
            };
            // This list changes based on order type
            if (this.order) {

                if (!this.canOrderAdmin && (this.order.orderStatus === this.orderService.ORDER_STATUS_ORDER_COMPLETE || this.order.orderStatus === this.orderService.ORDER_STATUS_ORDER_DELIVERED_AWAITING_APPROVAL)) {
                    this.canModifyOrder = false;

                    this.getForm('step1').disable();
                    this.getForm('step1').updateValueAndValidity();

                    this.getForm('step2').disable();
                    this.getForm('step2').updateValueAndValidity();

                    this.getForm('step3').disable();
                    this.getForm('step3').updateValueAndValidity();
                }

                //For existing orders, once the service group is saved to the db, we need to pass the service group to the service type lov
                this.populateServiceTypesLOV(this.order.serviceCategory, this.order.serviceGroup);
                if (this.canOrderAdmin) {
                    if (this.order.serviceCategory === this.orderService.SERVICE_CATEGORY_FULL_CONVERSION  || this.order.serviceCategory === this.orderService.SERVICE_CATEGORY_PARTIAL_CONVERSION) {
                        this.showIngestFile = true;
                    } else {
                        this.showIngestFile = false;
                    }
                }

                 // Hide grids if not service category Full or Partial
                this.showLlFormFunc(this.order.serviceGroup, this.getServiceTypeCode());
                // Show order description if service category is LDR, Report, or Publishing
                this.showOrderDescription = [this.orderService.SERVICE_GROUP_REPORT, this.orderService.SERVICE_GROUP_PUBLISHING].includes(this.order.serviceGroup);

                this.showTextboxesForChanges = (this.order.serviceGroup === this.orderService.SERVICE_GROUP_LABEL_CONVERSION);
                
                // Only show the textboxes if service group is label conversion
                this.showTextboxesForChanges = (this.order.serviceGroup === this.orderService.SERVICE_GROUP_LABEL_CONVERSION);
                // Populate form controls with the order data
                this.populateFormControls();

                // We have to do another call to get spl xml
                this.metaDataXmlFileName = '';
                this.metaDataXmlFilePath = '';
                if (this.order.dataCollectionForm && this.order.dataCollectionForm > 0) {
                    this.orderService.getFileStorageSplXml(this.order.dataCollectionForm)
                        .pipe(catchError(this.orderService.handleOrderObjectError))
                        .subscribe(response => {
                            const responseTyped: IApiTypedResult<SplFileStorageResult> = response as IApiTypedResult<SplFileStorageResult>;
                            if (responseTyped.isSuccess) {
                                if (responseTyped.data) {
                                    this.metaDataXmlFileName = responseTyped.data.fileName;
                                    this.metaDataXmlFilePath = responseTyped.data.filePath;
                                }
                            } else {
                                this.toastr.error('Unexpected error retrieving SPL XML file storage ' + responseTyped.message);
                            }
                        },
                            error => {
                            this.toastr.error(error.message);
                            });
                }
            }
            
            if (this.order && this.order.dataCollectionForm && this.llFormGridData) {
                // First check if the current selected Data Collection form are associated with the latest LL Revision
                const currentDataCollectionForm = this.llFormGridData.filter(x => x.llRevisionId === this.order.dataCollectionForm);
                if (currentDataCollectionForm && currentDataCollectionForm.length > 0) {
                    const selectedLlForm = currentDataCollectionForm[0];
                    if (selectedLlForm) {
                        this.llFormGridSelectedRowData = selectedLlForm;
                        this.syncLlFormGridSelection();
                        this.getBaselineSpls(selectedLlForm.llRevisionId);
                    }
                    this.cdRef.detectChanges();
                } else { // The selected dataCollectionFrom is associated with an older LL revisionID
                    this.bIsSelectedLLFormActive = false;
                    this.getLLByRevisionId(this.order.dataCollectionForm);
                }
            }


            let dataCollectionForm = this.activatedRoute.snapshot.queryParamMap.get('llRevisionId');
            if (dataCollectionForm && !this.order && this.canOrderAdmin) {
                this.getLLByRevisionId(parseInt(dataCollectionForm));
            }
            this.fileUploadAsyncSettings = {
                saveUrl: this.orderService.getFileUploadUrl()
            };

            // Don't show draft status on this page
            this.orderStatusesNoDraft = this.orderStatuses.filter(x => x.termValueDescription !== this.orderService.ORDER_STATUS_DRAFT)

            // Populate form controls with the order data
            this.populateFormControls();

            this.pageState = this.orderService.PAGE_STATE_READY;

            // Update grid selection permission
            if (this.llFormGrid) {
                this.llFormGrid.allowSelection = this.canEdit;
            }
            if (this.baselineSplGrid) {
                this.baselineSplGrid.allowSelection = this.canEdit;
          }

            this.isServiceTypeEnabled = this.isServiceCategorySelected && this.canEdit;
            this.isDeliveryPriorityEnabled = this.isServiceTypeSelected && this.canEdit;

            if (this.fileUpload) {
                (this.fileUpload as any).isForm = false;
            }
        });

        // Bind drop area to ejs-uploader
        this.dropElement = document.getElementById('dropArea');

        this.cdRef.detectChanges();
    }

    ngOnInit(): void {

        this.formInit();
        // Users can't change companies in the middle of viewing/editing an order
        this.utilService.toggleClientDropdownState(true);
        this.pageState = this.orderService.PAGE_STATE_LOADING;

        this.order = null;
        this.beforeOrderStatus = null;
        this.beforeConfirmedDate = null;
        this.canEdit = false;
        this.canOrderAdmin = false;
        this.canRequestCancellation = false;
        this.deliveryDoc = null;
        this.deliveryDocStatusChange = false;

        this.isServiceCategorySelected = false;
        this.freezeChangeEventsTemporarily = false;

        this.baselineSplGridSelectedId = null;
        this.wasFirstTimeBaselineSplGridSelectionDone = false;

        // Extract primary key from query string
        this.orderRevisionId = this.orderService.getNumberOrNull(this.activatedRoute.snapshot.params.orderRevisionId);

        // The guard says you need read permission to get to this page.
        // User might also have edit permissions.
        this.canEdit = this.authorizationService.hasPermission(this.orderService.PERMISSION_CAN_WRITE_ORDER);
        this.canOrderAdmin = this.authorizationService.hasPermission(this.orderService.PERMISSION_ORDER_ADMIN);

        // If new, force no admin
        if (!this.orderRevisionId) {
            this.canOrderAdmin = false;
        }

        // Display settings for grids
        this.llFormToolbarOptions = ['Search'];
        this.llFormInitialPage = { currentPage: 1, pageSize: 10, pageSizes: [10, 25, 50, 75, 100, 'All'] };
        this.llFormGridSelectionOptions = { type: 'Single', mode: 'Row' };

        this.baselineSplInitialPage = { currentPage: 1, pageSize: 10, pageSizes: [10, 25, 50, 75, 100, 'All'] };
        this.baselineSplSelectionOptions = { type: 'Single' };

        this.gridFormatOptions = { type: 'dateTime', format: Utilities.getLocaleDateString() + ' hh:mm a' };

        this.requestDeletionOfExistingDeliveryDoc = false;

        this.setFeatureFlag('ssdp-scs-delivery-ingestion');

        // Pull in file upload validation settings from appsettings.json
        const settings = this.settingsService.getSettings();
        this.validFileExtensionsStr = settings.ValidFileExtensions;
        this.fileMinSize = settings.FileMinSize;
        this.fileMaxSize = settings.FileMaxSize;

        this.adminFileMinSize = settings.FileMinSize;
        this.adminFileMaxSize = settings.FileMaxSize;
        this.dateFormat = Utilities.getLocaleDateString();

        this.initTabIndex();
    }

    initTabIndex() {
        (document.activeElement as HTMLElement).blur();

        const main = document.getElementById('firstDiv');

        if (main) {
          main.focus();
        }
    }

    confirmDlgButtonsBtnClickNo: EmitType<object> = () => {
        this.validForm = false;
        this.pageState = this.orderService.PAGE_STATE_READY;
        this.confirmDialog.hide();
    }

    confirmDlgButtonsBtnClickYes: EmitType<object> = () => {
        this.confirmDialog.hide();
        this.saveData("SUBMIT");
    }

    confirmDlgButtons: Object[] = [{ click: this.confirmDlgButtonsBtnClickNo.bind(this), buttonModel: { content: 'Cancel' } }, { click: this.confirmDlgButtonsBtnClickYes.bind(this), buttonModel: { content: 'Continue', isPrimary: true } }];

    validateAndSaveOrder(action: string) {
        if (action === 'SUBMIT' && this.showLlForm &&(this.getForm('step1', 'serviceCategory').value === "Full Conversion" || this.getForm('step1', 'serviceCategory').value === "Partial Conversion") && !this.llFormGridSelectedRowData) {
            this.confirmDialog.show();
        } else {
            this.saveData(action);
        }
    }

    // ------------------------------------------------------------
    // Save data
    // ------------------------------------------------------------
    saveData(action: string) {
        // Special case - "Submit Order" has additional validation rules.
        // They apply for fields that are on step 3.
        this.processedFilesCount = 0;

        this.pageState = this.orderService.PAGE_STATE_SAVING;

        // Clear out data from prior save
        this.postSaveDataMessage = null;
        this.postSaveDataNewOrderRevisionId = null;
        this.postSaveDataRedirectUrl = null;
        this.postSaveWaitingForDeliveryUpload = false;
        this.postSaveWaitingForSourceDocUpload = false;


        this.errorMessage = '';

        const modifiedByUser = this.storageManagerService.read('userData').name;

        // Request order cancellation is different enough let's handle it separately.
        if (action === 'CANCEL') {
            this.orderService.requestOrderCancellation(this.canRequestCancellation, this.order, modifiedByUser, '/order', false);
            this.pageState = this.orderService.PAGE_STATE_READY;
        } else {
            // stop here if form is invalid
            if (this.getForm('step1').invalid) {
                this.pageState = this.orderService.PAGE_STATE_READY;
                this.toastr.warning('Data is invalid.');
                return;
            } else {
                this.validForm = false;

                let dirty = this.orderService.isAngularFormDirty(this.getForm('step1') as UntypedFormGroup)
                || this.orderService.isAngularFormDirty(this.getForm('step2') as UntypedFormGroup)
                || this.orderService.isAngularFormDirty(this.getForm('step3') as UntypedFormGroup);
                // Also check fields in order admin box
                if (this.canOrderAdmin && this.getForm('admin')) {
                    dirty = dirty || this.orderService.isAngularFormDirty(this.getForm('admin'));
                }
                // Only save if dirty
                if ((dirty || action === 'SUBMIT') && (this.canEdit || this.canOrderAdmin)) {



                    if (!this.order && action !== 'SUBMIT') {
                        // New record
                        this.order = new Order();
                        this.order.orderStatus = this.orderService.ORDER_STATUS_DRAFT;
                    } else  if (!this.order) {
                        // New record
                        this.order = new Order();
                    }

                    // Accept optional query string param for dataCollectionForm
                    let dataCollectionForm = this.activatedRoute.snapshot.queryParamMap.get('llRevisionId');
                    if (dataCollectionForm) {
                        this.order.dataCollectionForm = parseInt(dataCollectionForm);
                    }

                    // Put back in prior values for certain fields
                    this.order.beforeOrderStatus = this.beforeOrderStatus;
                    this.order.beforeConfirmedDate = this.beforeConfirmedDate;

                    // Before we save any data, check if there are files waiting to be uploaded
                    let sourceDocsWaitingToBeUploaded = false;
                    if (this.fileUpload && this.fileUpload.getFilesData() && this.fileUpload.getFilesData().length) {
                        sourceDocsWaitingToBeUploaded = true;
                    }
                    let deliveryDocsWaitingToBeUploaded = false;
                    if (this.canOrderAdmin && this.adminFileUpload && this.adminFileUpload.getFilesData() && this.adminFileUpload.getFilesData().length > 0) {
                        deliveryDocsWaitingToBeUploaded = true;
                    }

                    // step1
                    this.order.serviceCategory = this.getForm('step1', 'serviceCategory').value;
                    this.order.serviceGroup = this.getForm('step1', 'serviceGroup').value;
                    this.order.country = this.getForm('step1', 'country')?.value && this.getForm('step1', 'country')?.value?.length ? this.getForm('step1', 'country').value : this.countrySelected;
                    this.order.splService = this.getServiceTypeObj(this.getForm('step1', 'serviceTypeId').value);
                    this.order.deliveryPriority = this.getForm('step1', 'deliveryPriority').value;
                    this.order.orderName = this.getForm('step1', 'orderName').value;

                    //step2
                    this.order.externalTrackingNumber = this.getForm('step2', 'externalTrackingNumber').value;
                    this.order.orderDescription = this.getForm('step2', 'orderDescription').value;

                    // step3
                    this.order.colOrDrugFactChangeInstruction = this.getForm('step3', 'colOrDrugFactChangeInstruction').value;
                    this.order.principalDisplayPanelChangeInstruction = this.getForm('step3', 'principalDisplayPanelChangeInstruction').value;
                    this.order.LLMetadataChangeInstruction = this.getForm('step3', 'LLMetadataChangeInstruction').value;
                    this.order.additionalInformation = this.getForm('step3', 'comments').value;

                    const orderOwners: OrderUser[] = this.orderService.userGuidsToOrderUsers(
                        this.allEmployeesInCompany, this.getForm('step1', 'drpOrderOwners')?.value, this.orderService.ORDER_USER_TYPE_ORDER_OWNER);
                    const orderRecipients: OrderUser[] = this.orderService.userGuidsToOrderUsers(
                        this.allEmployeesInCompany, this.getForm('step1', 'drpOrderRecipients')?.value, this.orderService.ORDER_USER_TYPE_ORDER_RECIPIENT);


                        // Pull the ID of the selected LL Form grid
                    this.order.dataCollectionForm = null;
                    if (this.llFormGridSelectedRowData) {
                        this.order.dataCollectionForm = this.llFormGridSelectedRowData.llRevisionId;
                    }

                    // Pull the ID of the baseline SPL
                    this.order.baselineWorkFlowOrderId = null;
                    if (this.baselineSplGridSelectedId) {
                        this.order.baselineWorkFlowOrderId = this.baselineSplGridSelectedId;
                    }

                    if (this.order.serviceCategory === this.orderService.SERVICE_CATEGORY_ESG) {
                        this.order.colOrDrugFactChangeInstruction = '';
                        this.order.principalDisplayPanelChangeInstruction = '';
                        this.order.LLMetadataChangeInstruction = '';
                    }



                    this.order.orderUsers = orderOwners.concat(orderRecipients);

                    // Save the admin fields if we're allowed
                    if (this.order && this.canOrderAdmin) {
                        this.order.orderStatus = this.getForm('admin', 'orderStatus').value;
                        if (this.getForm('admin', 'confirmedDueDate').value) {
                          let confirmedDueDateValue = this.getForm('admin', 'confirmedDueDate').value;
                          let confirmedDueDate: any = new Date(Date.UTC(
                            confirmedDueDateValue.getFullYear(),
                            confirmedDueDateValue.getMonth(),
                            confirmedDueDateValue.getDate()
                          )).toISOString();
                          this.order.confirmedDueDate = confirmedDueDate;
                        }

                        if (deliveryDocsWaitingToBeUploaded) {
                            // DeliveryDoc supplied, mark the actual delivery date
                            let actualDeliveryDateValue = new Date();
                            let actualDeliveryDate: any = new Date(Date.UTC(
                              actualDeliveryDateValue.getFullYear(),
                              actualDeliveryDateValue.getMonth(),
                              actualDeliveryDateValue.getDate()
                            )).toISOString();
                            this.order.actualDeliveryDate = actualDeliveryDate;
                        } else if (this.deliveryDoc && this.deliveryDoc.isDeleted) {
                            // DeliveryDoc was requested to be deleted, blank
                            // out actual delivery date
                            this.order.actualDeliveryDate = null;
                        }
                    }

                    if (action === 'SUBMIT') {
                        this.validForm = true;

                        if (!this.validateForSubmission()) {
                            this.validForm = false;
                            this.pageState = this.orderService.PAGE_STATE_READY;
                        }


                        // Special case for submit
                        this.order.orderStatus = this.orderService.ORDER_STATUS_PENDING;
                    } else {
                        this.validForm = true;
                    }

                    if (this.validForm) {
                        // Save to database
                        this.orderService.saveOrder(this.order, modifiedByUser).subscribe((response) => {
                            // Coerce to specific type
                            const responseTyped: IApiTypedResult<Order> = response as IApiTypedResult<Order>;
                            if (responseTyped.isSuccess) {
                                // Gather up settings so after async upload finishes we know where to go
                                this.postSaveDataMessage = responseTyped.message;
                                this.postSaveDataNewOrderRevisionId = responseTyped.data.orderRevisionId;
                                this.postSaveDataRedirectUrl = `/order/${this.postSaveDataNewOrderRevisionId}`;

                                if (action === 'STEP1' && !this.order.orderId && sourceDocsWaitingToBeUploaded ) {
                                    setTimeout(() => {
                                            this.postSaveWaitingForSourceDocUpload = true;
                                            let fileData = this.fileUpload.getFilesData();
                                            this.order.orderId = responseTyped.data.orderId;
                                            this.fileUpload.upload(fileData);
                                            return;

                                        }, 100);
                                } else if (action === 'SUBMIT') {
                                    // Step 1 doesn't have submit, but steps 1, 2, 3 save methods
                                    // are so complex, it makes my life easier to keep the same structure
                                    // across all
                                    setTimeout(() => {
                                        if (sourceDocsWaitingToBeUploaded) {
                                            this.postSaveWaitingForSourceDocUpload = true;
                                            let fileData = this.fileUpload.getFilesData();
                                            this.order.orderId = responseTyped.data.orderId;
                                            this.isNewOrder = true;
                                            this.fileUpload.upload(fileData);
                                            return;

                                        }

                                        this.pageState = this.orderService.PAGE_STATE_READY;
                                        this.orderService.alert('Order Submission Successful', 'Your order request has been submitted. One of our Project Coordinators is in the process of reviewing your order. Once the order has been reviewed you will receive a confirmation email that includes the expected delivery date.', () => {
                                            this.router.navigate(['/order-tracker']).then(() => {
                                                this.pageState = this.orderService.PAGE_STATE_READY;
                                                window.scrollTo(0, 0);
                                            })
                                        });
                                    }, 100);
                                } else {
                                    // Regular save of Order object successful, now do optional file uploads.
                                    //
                                    // We just set the property on this component postSavedatNewOrderRevisionId
                                    // which is shared with the child component OrderAdminComponent,
                                    // sounds great BUT angular timing messes it up.  The child component
                                    // can't see the value immediately.  The child component can't do setTimeout
                                    // because the upload is already in progress.  So, we make the parent setTimeout
                                    // (sleep) right now so angular child component catches up with bound value for
                                    // and can save the uploaded doc to correct postSavedatNewOrderRevisionId
                                    setTimeout(() => {

                                        // Now that the order is saved, upload docs to *new* OrderRevisionId from
                                        // potentially sourceDocs and delivery (inside child component)
                                        if (!sourceDocsWaitingToBeUploaded && !deliveryDocsWaitingToBeUploaded) {
                                            // No files to upload, we're done.
                                            this.postSaveData();
                                        } else {
                                            // no sourceDoc files from this page
                                            // Asynchronously upload delivery file from admin child componet
                                            if (deliveryDocsWaitingToBeUploaded) {
                                                // upload the files
                                                this.postSaveWaitingForDeliveryUpload = true;
                                                this.adminFileUpload.upload(this.adminFileUpload.getFilesData());  // this is async
                                            }
                                            // Upload sourceDoc files from this page
                                            if (sourceDocsWaitingToBeUploaded) {
                                                this.postSaveWaitingForSourceDocUpload = true;
                                                this.fileUpload.upload(this.fileUpload.getFilesData()); // this is async
                                            }
                                            // Nothing left for now, wait for events to get triggered.
                                            return;
                                        }
                                    }, 100);
                                }
                            } else {
                                this.orderService.handleOrderError(this, responseTyped, 'Order save failed', true)
                            }
                        },
                            error => this.orderService.handleOrderError(this, error, 'Order save failed', true)
                        );
                        this.getForm('step1').markAsPristine();
                    }
                } else {
                    this.pageState = this.orderService.PAGE_STATE_READY;
                    this.toastr.warning('No changes to save.');
                }
            }

        }
    }

    // ------------------------------------------------------------
    // Submission validation
    // ------------------------------------------------------------

    // Before official order submission, validate additional fields
    // Regular "save as draft" doesn't do this level of validation.
    validateForSubmission() {
        let message = '';
        let isValid = true;

        if (!this.getForm('step1').valid) {
            isValid = false;
            message += 'Order type data is invalid.  ';
        }

        // Require order type
        if (!this.order.splService ) {
            isValid = false;
            message += 'Order type cannot be blank.  ';
        }

        // Require order name
        if (!this.order.orderName) {
            isValid = false;
            message += 'Order name cannot be blank.  ';
        }

        // Require delivery priority
        if (this.order && !this.order.deliveryPriority) {
            isValid = false;
            message += 'Delivery priority cannot be blank.  ';
        }

        // Require at least one order owner
        let orderOwners: OrderUser[] = [];
        if (this.order.orderUsers) {
            orderOwners = this.order.orderUsers.filter(x => x.orderUserTypeId === this.orderService.ORDER_USER_TYPE_ORDER_OWNER);
        }
        if (!orderOwners || (orderOwners.length == 0)) {
            isValid = false;
            message += 'At least one order owner must be supplied.  ';
        }

        // Note: data collection form and baseline SPL are never required for submission.

        if (!isValid) {
            message = 'Unable to submit order.  Please ensure required data is supplied before submitting order.  ' + message;
            // turn off spinner before message
            this.pageState = this.orderService.PAGE_STATE_READY;
            this.orderService.alert('Order Submission Failed', message);
        }
        return isValid;
    }

    // ------------------------------------------------------------
    // Existing file list
    // ------------------------------------------------------------
    deleteExistingFile(index: number) {
        if (!this.canModifyOrder) {
            return;
        }
        this.order.sourceDocuments[index].isDeleted = true;
        this.getForm('step2').markAsDirty();
        this.getForm('step2').markAllAsTouched();
      }

      undeleteExistingFile(index: number) {
        if (!this.canModifyOrder) {
            return;
        }
          this.order.sourceDocuments[index].isDeleted = false;
          this.getForm('step2').markAsDirty();
          this.getForm('step2').markAllAsTouched();
      }


    // ------------------------------------------------------------
    // File upload
    // ------------------------------------------------------------

    // Trigger a browse window
    browseClick(type: string) {
        if (!this.canModifyOrder) {
            return;
        }
        if (type === this.orderService.UPLOAD_FILE_TYPE_SOURCE_DOC) {
            document.getElementsByClassName('e-file-select-wrap')[0].querySelector('button').click(); return false;
        }
        if (type === this.orderService.UPLOAD_FILE_TYPE_DELIVERY) {

            let allBrowseButtonWrappers = document.getElementsByClassName('e-file-select-wrap');
            let orderAdminBrowseWrapper = allBrowseButtonWrappers[0];
            if (allBrowseButtonWrappers.length > 1) {
                orderAdminBrowseWrapper = allBrowseButtonWrappers[1];
            }
            orderAdminBrowseWrapper.querySelector('button').click();
            return false;
        }
    }

    public onSelected: EmitType<SelectedEventArgs> = (args: any, type: string) => {
        // Mark as dirty so we know user has to hit save button
        if (type === this.orderService.UPLOAD_FILE_TYPE_SOURCE_DOC && this.canModifyOrder) {
            this.getForm('step2').markAsDirty();
            this.getForm('step2').markAsTouched();
        }
        if (type === this.orderService.UPLOAD_FILE_TYPE_DELIVERY && this.canModifyOrder) {
            this.getForm('admin').markAsDirty();
            this.getForm('admin').markAsTouched();
        }
    }

    onUploadFailure(args, type: string) {
        let response = JSON.parse(args.e.currentTarget.response);
        if (response && response.length) {
            let reason = response.split('Reason: ');
            if (reason.length > 1) {
                args.statusText = reason[1];
            }
        }

        if (type === this.orderService.UPLOAD_FILE_TYPE_SOURCE_DOC) {
            this.hasFailedFiles = true;
            this.processedFilesCount++;

            this.toastr.error('Error uploading source document ' + args.file.name);
            if (this.processedFilesCount == this.fileUpload.getFilesData().length) {
                if (this.isNewOrder) {
                    this.showAlertMessage();
                }
            }
        }
        if (type === this.orderService.UPLOAD_FILE_TYPE_DELIVERY) {
            this.onOrderAdminFileUpload('failure');
        }
    }

    
    public onSuccess: EmitType<Object> = (args: any) => {
        // Successfully uploaded one file.
        this.processedFilesCount++;

        if (this.processedFilesCount == this.fileUpload.getFilesData().length) {
            if (this.isNewOrder) {
                this.showAlertMessage();
            }
        }
        
    }

    showAlertMessage(){
        this.pageState = this.orderService.PAGE_STATE_READY;
        let message = 'Your order request has been submitted. One of our Project Coordinators is in the process of reviewing your order. Once the order has been reviewed you will receive a confirmation email that includes the expected delivery date.';
        let navLink = '/order-tracker';
        
        if (this.hasFailedFiles) {
            message = 'Your order request has been submitted, One or more files were invalid, please check the files and try again. One of our Project Coordinators is in the process of reviewing your order. Once the order has been reviewed you will receive a confirmation email that includes the expected delivery date.';
            navLink = this.postSaveDataRedirectUrl;
        }

        this.orderService.alert('Order Submission Successful',
            message, () => {
            this.router.navigate([navLink]).then(() => {
                this.pageState = this.orderService.PAGE_STATE_READY;
                window.scrollTo(0, 0);
            })
        });
}


    // Upload sourceDoc file to order, validate settings
    public onUploading: EmitType<UploadingEventArgs> = (args: any, type: string) => {
        if (type === this.orderService.UPLOAD_FILE_TYPE_SOURCE_DOC && this.canModifyOrder) {
            this.orderService.uploadOrderDoc(args,
                this.order.orderId,
                this.postSaveDataNewOrderRevisionId,
                this,
                this.getForm('step2'),
                false,
                this.orderService.UPLOAD_FILE_TYPE_SOURCE_DOC);
        }
        if (type === this.orderService.UPLOAD_FILE_TYPE_DELIVERY && this.canModifyOrder) {
            if (this.order.serviceCategory === this.orderService.SERVICE_CATEGORY_ESG) {
                this.ingestFileValue = false;
            }
            this.orderService.uploadOrderDoc(args,
                this.order.orderId,
                this.postSaveDataNewOrderRevisionId,
                this,
                this.getForm('admin'),
                false,
                this.orderService.UPLOAD_FILE_TYPE_DELIVERY,
                this.ingestFileValue);
        }
    }


    // Helper function for dealing with angular interaction
    // between order types and service types and underlying reference data
    getServiceTypeObj(serviceTypeId: string): ILLGenericKeys {
        const matches = this.serviceTypesLOV.filter(x => x.code === serviceTypeId);
        if (matches) {
            // Huh?  If these values are null during save I get an error 'source required'
            // Set them this way and it works.  I don't know why.
            matches[0].relatedKeys = [];
            matches[0].relatedKeyLabels = '';
            return matches[0];
        } else {
            return null;
        }
    }

    setFeatureFlag(name: string) {
        this.featureService.IsFeatureEnabled(name)
        .subscribe(data => {
          if (data) {
            this.isScsIngestFileFeatureVisible = data
          }
          else {
            this.isScsIngestFileFeatureVisible = false;
          }
        }, error => {
          // An error has occured. For safe measure, hide the feature.
          this.isScsIngestFileFeatureVisible = false;

          // Log to the console since we do not want to alert the user to a feature they may not have
          console.log(`An error has occured checking if the following feature is enabled: ${name}`)
        });
    }

    // After order is saved AND all files are uploaded via order admin
    // and Step 2 file uploader (not easy because they are all async)
    // then go ahead and give a message and redirect.
    // Only step 2 has dual file uploaders - source & delivery,
    // but for consistency I'm copy/pasting this function across
    // all three steps.
    postSaveData() {
        // Are we still waiting for sourceDoc upload or delivery upload?
        if (this.postSaveWaitingForSourceDocUpload || this.postSaveWaitingForDeliveryUpload) {
            // Yup, keep waiting.
            return;
        }

        this.toastr.success(this.postSaveDataMessage);

        // Force angular to rerun ngOnInit to reload data when navigating to address
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
        this.router.onSameUrlNavigation = 'reload';

        this.router.navigate([this.postSaveDataRedirectUrl]).then(() => {
            this.pageState = this.orderService.PAGE_STATE_READY;
            // Clear out admin file upload control if relevant
            if (this.canOrderAdmin && this.adminFileUpload) {
                this.adminFileUpload.clearAll();
            }
            window.scrollTo(0, 0);
        });
    }

    formInit() {
        this.orderForm = this.fb.group({
            step1: this.fb.group({
                orderName: ['', { validators: [Validators.required] }],
                serviceCategory: [{ value: '', disabled: true }, this.showOrderType ? Validators.required : null],
                deliveryPriority: ['', { validators: [Validators.required] }],
                serviceTypeId: ['', { validators: [Validators.required] }],
                drpOrderOwners: [''],
                drpOrderRecipients: [''],
                serviceGroup: ['', { validators: [Validators.required] }],
                country: [''],
            }),
            step2: this.fb.group({
                externalTrackingNumber: [''],
                orderDescription: [''],
                llFormGrid: [''],
                baselineSplGrid: [''],
                fileUpload: ['']
            }),
            step3: this.fb.group({
                colOrDrugFactChangeInstruction: [''],
                principalDisplayPanelChangeInstruction: [''],
                LLMetadataChangeInstruction: [''],
                comments: [''],
            }),
            admin: this.fb.group({
                orderStatus: [''],
                confirmedDueDate: [''],
                adminFileUpload: [''],
                ingestFile: [true]
            })

        });
    }

    ddwChanged(event) {
        if (event.isInteracted) {
            this.getForm('step1').markAsDirty();
        }
    }

    isOrderTypeSelected(): boolean {
        return this.getForm('step1', 'serviceCategory').value !== '';
    }

    serviceGroupChanged: EmitType<ChangeEventArgs> = (event: any) => {
        // Prevent unwanted nested change events
        if (this.freezeChangeEventsTemporarily) {
            return;
        }

        const oldServiceGroup = this.getForm('step1', 'serviceGroup').value;

        this.showTextboxesForChanges = (event.itemData.value === this.orderService.SERVICE_GROUP_LABEL_CONVERSION);

        if (this.order && event.itemData.value !== this.order.serviceGroup) {
            if (confirm(
                `Are you sure you want to change the service group?  This change will blank out dependent values.`)) {
                // reset the whole order form
                this.getForm('step1').reset();
                // Order Owner list and Order Recipient list
                this.populateEmployeeLists();

                if (this.order) {
                    this.getForm('step1').patchValue({
                        country: this.countrySelected,
                        serviceGroup: event.itemData.value,
                        drpOrderOwners: this.orderService.getUserGuidsFromOrderUsers(
                            this.allEmployeesInCompany, this.order.orderUsers, this.orderService.ORDER_USER_TYPE_ORDER_OWNER),
                        drpOrderRecipients: this.orderService.getUserGuidsFromOrderUsers(
                            this.allEmployeesInCompany, this.order.orderUsers, this.orderService.ORDER_USER_TYPE_ORDER_RECIPIENT)
                    });
                    this.resetOrderData();
                } else {
                    this.getForm('step1').patchValue({
                        country: this.countrySelected,
                        serviceGroup: event.itemData.value,
                    });
                }
            } else {
                setTimeout(() => {
                    this.freezeChangeEventsTemporarily = true;
                    this.getForm('step1').patchValue({
                        country: this.countrySelected,
                        serviceGroup: oldServiceGroup });
                    this.freezeChangeEventsTemporarily = false;
                }, 200);
                return;
            }
        }

        if (event.itemData) {
            this.isServiceGroupSelected = true;

            //check if the service group is ESG or ER or Reports
            //then add or remove form field validation for categoruy
            if (event.itemData.value === this.orderService.SERVICE_GROUP_ESG || event.itemData.value === this.orderService.SERVICE_GROUP_ER || event.itemData.value === this.orderService.SERVICE_GROUP_REPORT) {
                    this.getForm('step1', 'serviceCategory').setValidators(null);
                    this.getForm('step1', 'serviceCategory').updateValueAndValidity();
            } else {
                // if the validation is removed, then add it back
                this.getForm('step1', 'serviceCategory').setValidators(Validators.required);
                this.getForm('step1', 'serviceCategory').updateValueAndValidity();
            }

            this.getForm('step1', 'serviceCategory').enable();
            this.getForm('step1', 'serviceCategory').updateValueAndValidity();

            if (this.order) {
                this.order.serviceGroup = event.itemData.value;
            }
            this.serviceTypesLOV = [];
            this.getForm('step1').patchValue({ serviceCategory: '' });
            this.checkOrderType(event.itemData.value);
            if (!this.showOrderType) {
                this.getForm('step1').patchValue({ serviceCategory: '' });
                this.isServiceCategorySelected = true;
                this.populateServiceTypesLOV(null, event.itemData.value);
                this.serviceTypesLOV = this.serviceTypesLOV.filter(x => x.relatedKeyLabels  === event.itemData.value);
            }
            this.updateDeliveryPrioritiesVisibility(null);
            // Hide grids if not service category Full or Partial
            this.showLlForm = [this.orderService.SERVICE_GROUP_ESG, this.orderService.SERVICE_GROUP_ER, this.orderService.SERVICE_GROUP_REMS, this.orderService.SERVICE_GROUP_LABEL_CONVERSION].includes(event.itemData.value ?? this.order.serviceGroup);
            // Show order description if service category is LDR, Report, or Publishing
            this.showOrderDescription = [this.orderService.SERVICE_GROUP_REPORT, this.orderService.SERVICE_GROUP_PUBLISHING].includes(event.itemData.value ?? this.order?.serviceGroup);
        }


    }

    showLlFormFunc(serviceGroup, serviceType) {
        if (serviceGroup === this.orderService.SERVICE_GROUP_REPORT) {
            if(["SPL-LDR-KIT", "SPL-LDR-REG"].includes(serviceType)) {
                this.showLlForm = true;
            } else {
                this.showLlForm = false;
            }
        } else {
        this.showLlForm = [this.orderService.SERVICE_GROUP_ESG, this.orderService.SERVICE_GROUP_ER, this.orderService.SERVICE_GROUP_REMS, this.orderService.SERVICE_GROUP_LABEL_CONVERSION].includes(serviceGroup);
      }
        if (this.countrySelected == 'Canada') {
            this.showLlForm = false;
        }
    }

    serviceCategoryChanged: EmitType<ChangeArgs> = (event: any) => {
        // Prevent unwanted nested change events
        if (this.freezeChangeEventsTemporarily) {
            return;
        }

        const oldServiceCategory = this.getForm('step1', 'serviceCategory').value;
        const newServiceCategory = event.value;
        let isDeliveryPrioritySelected = false;
        if (this.getForm('step1', 'deliveryPriority') && this.getForm('step1', 'deliveryPriority').value) {
            isDeliveryPrioritySelected = true;
        }
        // If service category changes
        // blank out dependent fields service type and delivery priority.
        // But don't bother if serviceType and deliveryPriority are still blank.
        if ((this.isServiceTypeSelected || isDeliveryPrioritySelected)
            && oldServiceCategory
            && (oldServiceCategory !== newServiceCategory)) {
            // Confirm with user that we're blanking out dependent fields
            if (!confirm('Are you sure you want to change the type of order?  This change will blank out dependent values such as service type and delivery priority.')) {
                setTimeout(() => {
                    this.freezeChangeEventsTemporarily = true;
                    this.getForm('step1').patchValue({ serviceCategory: oldServiceCategory });
                    this.freezeChangeEventsTemporarily = false;
                }, 200);
                return;
            }

            // User does want to continue with the change
            this.getForm('step1').patchValue({
                serviceTypeId: null,
                deliveryPriority: null
            });
            this.isServiceTypeSelected = false;

            this.isServiceCategorySelected = true;
            if (newServiceCategory) {
                var serviceGroup = this.getForm('step1', 'serviceGroup').value;
                this.populateServiceTypesLOV(newServiceCategory, serviceGroup);
                this.populateServiceGroupLOV();
            }

            this.updateDeliveryPrioritiesVisibility(null);
        } else {
            this.isServiceCategorySelected = true;
            if (newServiceCategory) {
                var serviceGroup = this.getForm('step1', 'serviceGroup').value;
                this.populateServiceTypesLOV(newServiceCategory, serviceGroup);
            }
        }

        if (this.canOrderAdmin) {
            if (newServiceCategory === this.orderService.SERVICE_CATEGORY_FULL_CONVERSION  || newServiceCategory === this.orderService.SERVICE_CATEGORY_PARTIAL_CONVERSION) {
                this.showIngestFile = true;
            } else {
                this.showIngestFile = false;
            }
        }

    }

    // Change grid filter operator from "startwith" to "contains" for columns that are string
    public begin(args: any): void {
        if (args.requestType === 'filtering') {
            if (args.columns) {
                args.columns.forEach(col => {
                    switch (args.currentFilteringColumn) {
                        case 'modifiedDate':
                            break;
                        default:
                            col.operator = 'contains';       // set the “contains” operator for the column
                            break;
                    }
                });
            }
        }
    }

    // Track if a service type is selected
    serviceTypeChanged: EmitType<ChangeEventArgs> = (event: any) => {
        // Prevent unwanted nested change events
        if (this.freezeChangeEventsTemporarily) {
            return;
        }

        this.isServiceTypeSelected = false;
        if (event.itemData) {
            this.isServiceTypeSelected = true;
            const serviceCategory = this.getForm('step1', 'serviceCategory').value;
            const oldServiceType = this.getForm('step1', 'serviceTypeId').value;
            const newServiceType = event.value;
            let isDeliveryPrioritySelected = false;
            if (this.getForm('step1', 'deliveryPriority').value) {
                isDeliveryPrioritySelected = true;
            }

            // If delivery priority was already selected,
            // and we are changing service type,
            // confirm with user
            // that we're about to wipe it out
            if (isDeliveryPrioritySelected
                && oldServiceType
                && (oldServiceType !== newServiceType)) {

                this.freezeChangeEventsTemporarily = true;
                if (confirm(
                    `Are you sure you want to change the serviceType?  This change will blank out dependent values, namely the delivery priority.`)) {
                    // User chose OK
                    // Delivery priority depends on service type, so blank it out upon change
                    this.getForm('step1').patchValue({
                        deliveryPriority: null
                    });
                    this.updateDeliveryPrioritiesVisibility(newServiceType);
                    this.showLlFormFunc(this.getForm('step1', 'serviceGroup').value, newServiceType);

                    this.freezeChangeEventsTemporarily = false;
                } else {
                    // User chose cancel
                    // Undo selection
                    setTimeout(() => {
                        this.getForm('step1').patchValue({ serviceTypeId: oldServiceType });
                        this.isServiceTypeSelected = true;
                        this.freezeChangeEventsTemporarily = false;
                    }, 200);
                }
            } else {
                // Don't need to confirm anything with user because dependent fields
                // are blank, go ahead and update delivery priorities visibility
                this.updateDeliveryPrioritiesVisibility(newServiceType);
                this.showLlFormFunc(this.getForm('step1', 'serviceGroup').value, newServiceType);

            }
        }
    }

    // ------------------------------------------------------------
    // Order admin is a subcomponent, and emits up certain events
    // usually about the delivery doc
    // ------------------------------------------------------------
    onOrderAdminFileUpload(event) {
        if (event === 'success') {
            // Successfully uploaded delivery doc
            this.postSaveWaitingForDeliveryUpload = false;
            this.postSaveData();
        } else if (event === 'failure') {
            // Attempted to upload delivery doc but failed
            this.pageState = this.orderService.PAGE_STATE_READY;
            this.toastr.error('Unexpected error uploading delivery document');
        } else if (event === 'deleteExistingDeliveryDoc') {
            // Request that existing delivery doc be deleted with next save action
            if (this.deliveryDoc) {
                this.deliveryDocStatusChange = true;
                this.deliveryDoc.isDeleted = true;
            }
            this.pageState = this.orderService.PAGE_STATE_READY;
        } else if (event === 'undeleteExistingDeliveryDoc') {
            // Undo request that existing delivery doc be deleted with next save action
            if (this.deliveryDoc) {
                this.deliveryDocStatusChange = false;
                this.deliveryDoc.isDeleted = false;
            }
            this.pageState = this.orderService.PAGE_STATE_READY;
        } else if (event === 'onIngestFileChange') {

            if (this.deliveryDoc) {
                this.deliveryDoc.ingestFile = this.ingestFileValue;
            }
            this.pageState = this.orderService.PAGE_STATE_READY;

        }
    }

    // ------------------------------------------------------------
    // Load data
    // ------------------------------------------------------------

    // Get data from the resolver and populate various dropdownlists
    // and main order fields and store it in class state.
    loadData(): void {
        // Get data from resolver used in routing module
        const orderDataResolved = this.activatedRoute.snapshot.data['orderDataResolved'];
        if (orderDataResolved.error) {
            this.toastr.error(orderDataResolved.error);
        } else {
            if (orderDataResolved) {
                // OrderResolver returns a fixed array of data.

                // Employees of this company
                if (!orderDataResolved[0]) {
                    this.orderService.handleOrderError(this, null, 'Failed to get list of employees in company', false);
                }
                this.allEmployeesInCompany = orderDataResolved[0];

                // Reference lists
                if (!orderDataResolved[1]) {
                    this.orderService.handleOrderError(this, null, 'Failed to get reference data (service type)', false);
                }
                this.serviceCategoryToServiceTypeAssoc = orderDataResolved[1];

                if (!orderDataResolved[2]) {
                    this.orderService.handleOrderError(this, null, 'Failed to get reference data (priority)', false);
                }
                this.serviceTypeToDeliveryPriorityAssoc = orderDataResolved[2];

                // Main order
                if (!orderDataResolved[3] && this.orderRevisionId) {
                    this.orderService.handleOrderError(this, null, 'Failed to load order', false);
                }

                let orderApiResult: IApiTypedResult<Order> = orderDataResolved[3];
                if (orderApiResult) {
                    if (orderApiResult.isSuccess) {
                        this.order = orderApiResult.data;
                        if (this.order) {
                            this.countrySelected = this.order.country;
                        }
                        // Special case - if order owner contains a record that is not present, display warning on UI
                        this.inactiveOrderOwnersList = this.orderService.GetInactiveOrderUsers(this.allEmployeesInCompany, this.order.orderUsers, this.orderService.ORDER_USER_TYPE_ORDER_OWNER);
                        // If order email recipient contains a record that is not present, display warning on UI
                        this.inactiveOrderEmailRecipientList = this.orderService.GetInactiveOrderUsers(this.allEmployeesInCompany, this.order.orderUsers, this.orderService.ORDER_USER_TYPE_ORDER_RECIPIENT);;

                        // Remember original values for certain fields
                        this.beforeOrderStatus = this.order.orderStatus;
                        this.beforeConfirmedDate = this.order.confirmedDueDate;

                        // Only user with "SSDP.CanUpateOrder" permission can edit an order if the order status is "Order Delivered - Awaiting Approval" or "completed"
                        if (!this.canOrderAdmin && (this.order.orderStatus === this.orderService.ORDER_STATUS_ORDER_DELIVERED_AWAITING_APPROVAL
                            || this.order.orderStatus === this.orderService.ORDER_STATUS_ORDER_COMPLETE)) {
                            this.canEdit = false;
                        }

                        // Don't allow canOrderAdmin if draft
                        if (this.order.orderStatus === this.orderService.ORDER_STATUS_DRAFT) {
                            this.canOrderAdmin = false;
                        }

                        // // Can only submit if it's draft
                        if (this.order.orderStatus === this.orderService.ORDER_STATUS_DRAFT) {
                            this.canSubmitOrder = true;
                        }

                        // If still canOrderAdmin, recaption save
                        if (this.canOrderAdmin) {
                            this.saveButtonCaption = 'Update Order';
                        }

                        // Non-admin users can cancel pending or inprocess if they have the perm
                        if (!this.canOrderAdmin
                            && ([this.orderService.ORDER_STATUS_PENDING, this.orderService.ORDER_STATUS_IN_PROCESS].indexOf(this.order.orderStatus) > -1)
                            && this.authorizationService.hasPermission(this.orderService.PERMISSION_CAN_CANCEL_ORDER)) {
                            this.canRequestCancellation = true;
                        }

                        // Pull out the deliveryDoc and sourceDocs separately
                        // order.sourceDocuments is an array mixed with both deliveryDoc and SourceDocs.
                        if (this.order && this.order.sourceDocuments) {
                            this.deliveryDoc = null;
                            let deliveryDocs = this.order.sourceDocuments.filter(x => x.mediaFileType === this.orderService.UPLOAD_FILE_TYPE_DELIVERY);
                            if (deliveryDocs && deliveryDocs.length > 0) {
                                this.deliveryDoc = deliveryDocs[0];
                            }
                            // Step 2 - sourceDocs
                            (this.order.sourceDocuments as any).find(x => x.mediaFileType === this.orderService.UPLOAD_FILE_TYPE_DELIVERY);
                            this.sourceDocs = this.order.sourceDocuments.filter(x => x.mediaFileType === this.orderService.UPLOAD_FILE_TYPE_SOURCE_DOC);

                        }
                    } else {
                        this.orderService.handleOrderError(this, orderApiResult, 'Failed to load order', false);
                    }
                } else {
                    this.canSubmitOrder = true;
                }

                // Update LL forms array (take only active records)
                if (orderDataResolved[4]) {
                    this.llFormGridData = orderDataResolved[4].data.filter(x => x.isDraftMode === false);
                }

                if (!orderDataResolved[5]) {
                    this.orderService.handleOrderError(this, null, 'Failed to load order statuses', false);
                }
                this.orderStatuses = orderDataResolved[5];

                if (!orderDataResolved[7]) {
                    this.orderService.handleOrderError(this, null, 'Failed to get country service group data', false);
                }
                this.countryServiceGroup = orderDataResolved[7];

                if (!orderDataResolved[8]) {
                    this.orderService.handleOrderError(this, null, 'Failed to get country service type data', false);
                }
                this.countryServiceType = orderDataResolved[8];
            }
        }
    }

    // Populate list of employees in Order Owners and Order Recipients lists
    populateEmployeeLists(): void {
        // Prevent unwanted nested change events
        if (this.freezeChangeEventsTemporarily) {
            return;
        }

        if (this.allEmployeesInCompany) {
            // Reformat from user objects list to pulldown list of ILLGenericKeys
            this.orderOwnersLOV = this.allEmployeesInCompany
                .map(emp => new class implements ILLGenericKeys {
                    code = emp.userGuid;
                    label = emp.firstName + ' ' + emp.lastName;
                    id = null;
                    name = null;
                    relatedKeyLabels = null;
                    relatedKeys = null;
                });
            // Sort by name
            this.orderOwnersLOV.sort((a, b) => a.label.localeCompare(b.label));
            // Copy same employees to email list (for Object to "any" to use newer .assign method without VS warning
            this.orderRecipientsLOV = this.orderOwnersLOV.map(x => (Object as any).assign({}, x));
        }
    }

    // Populate the order data
    populateFormControls(): void {
        // Prevent unwanted nested change events
        if (this.freezeChangeEventsTemporarily) {
            return;
        }


        if (this.orderRevisionId == null) {
            // New record, use defaults
            const userGuid = this.storageManagerService.read('userGuid');
            this.getForm('step1').patchValue({
                serviceCategory: '',
                serviceTypeId: null,
                deliveryPriority: this.orderService.DELIVERY_PRIORITY_REGULAR,
                orderName: null,
                drpOrderOwners: [userGuid],
                drpOrderRecipients: [],
                serviceGroup: '',
                country: this.countrySelected
            });
        } else {
            if (this.order.serviceGroup && this.canModifyOrder) {
                this.getForm('step1', 'serviceCategory').enable();
                this.getForm('step1', 'serviceCategory').updateValueAndValidity();
            }
            this.countrySelected = this.order && this.order?.country && this.order?.country?.length ? this.order?.country : this.countrySelected;
            this.getForm('step1').patchValue({
                serviceCategory: this.order ? this.order.serviceCategory : 0,
                serviceTypeId: this.getServiceTypeCode(),
                serviceGroup: this.order ? this.order.serviceGroup : null,
                country: this.order && this.order?.country && this.order?.country?.length ? this.order?.country : this.countrySelected,
                deliveryPriority: this.order ? this.order.deliveryPriority : 0,
                orderName: this.order.orderName,
                // Feed a string array of userGuids to pulldown lists
                drpOrderOwners: this.orderService.getUserGuidsFromOrderUsers(
                    this.allEmployeesInCompany, this.order.orderUsers, this.orderService.ORDER_USER_TYPE_ORDER_OWNER),
                drpOrderRecipients: this.orderService.getUserGuidsFromOrderUsers(
                    this.allEmployeesInCompany, this.order.orderUsers, this.orderService.ORDER_USER_TYPE_ORDER_RECIPIENT)
            });

            this.getForm('step2').patchValue({
                externalTrackingNumber: this.order.externalTrackingNumber,
                orderDescription: this.order.orderDescription,
                // Can't patch grids here, but you can set row index in AfterViewInit
            });

            this.getForm('step3').patchValue({
                colOrDrugFactChangeInstruction: this.order.colOrDrugFactChangeInstruction,
                principalDisplayPanelChangeInstruction: this.order.principalDisplayPanelChangeInstruction,
                LLMetadataChangeInstruction: this.order.LLMetadataChangeInstruction,
                comments: this.order.additionalInformation
            });

            // Convert UTC date string to a Date object that represents the same calendar date in local time
            let confirmedDueDateForPicker = null;
            if (this.order.confirmedDueDate) {
                const utcDate = new Date(this.order.confirmedDueDate);
                confirmedDueDateForPicker = new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate());
            }

            this.getForm('admin').patchValue({
                orderStatus: this.order.orderStatus,
                confirmedDueDate: confirmedDueDateForPicker,
            });
        }

        // Track if certain controls are filled out because other controls' enabled state depend on them
        if (this.getForm('step1', 'serviceCategory').value) {
            this.isServiceCategorySelected = true;
        } else {
            this.isServiceCategorySelected = false;
        }
        if (this.getForm('step1', 'serviceTypeId').value) {
            this.isServiceTypeSelected = true;
        } else {
            this.isServiceTypeSelected = false;
        }
        if (this.getForm('step1', 'serviceGroup').value) {
          this.isServiceGroupSelected = true;
        }
        else {
          this.isServiceGroupSelected = false;
        }

        if (this.order) {
            this.updateDeliveryPrioritiesVisibility(this.getServiceTypeCode());
        }
    }

    // download sourceDoc
    downloadFile(filePath: string, fileName: string) {
        this.orderService.downloadFile(filePath, fileName).subscribe(response => {
            this.orderService.postDownloadFile(response, fileName);
        },
          error => {
            this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
          });
    }

    // Populate pulldown list of serviceType based on serviceCategory.
    populateServiceTypesLOV(serviceCategory: string, serviceGroup: string): void {
        // Prevent unwanted nested change events
        if (this.freezeChangeEventsTemporarily) {
            return;
        }

        this.serviceTypesLOV = [];
        let countryServiceTypesCodes = this.countryServiceType .filter(x => x.parentTermValueName === this.countrySelected)
            .map(x => x.childTermValueCode);

        // If all data present,
        // return a list of ILLGenericKeys filtered
        // from the list of IBusinessOperationQualifier by parentCode===serviceCategory
        if (this.serviceCategoryToServiceTypeAssoc) {
          this.serviceTypesLOV = this.serviceCategoryToServiceTypeAssoc
              .filter(x => {
                  const matchesParentFilter = x.parentTermListName === serviceGroup;
                  const matchesServiceType = x.childTermListName === 'Service Type';
                  const matchesServiceCategory = serviceCategory ? x.parentTermValueName === serviceCategory : true;
                  const matchesCountryServiceCat = countryServiceTypesCodes ? countryServiceTypesCodes?.includes(x.childTermValueCode) : true;
                  return matchesParentFilter && matchesServiceType && matchesServiceCategory && matchesCountryServiceCat;
              })
              .map(x =>
                  new class implements ILLGenericKeys {
                      code = x.childTermValueCode;
                      label = x.childTermValueName;
                      id = null;
                      name = null;
                      relatedKeyLabels = x.parentTermListName;
                      relatedKeys = null;
                  })
              .sort((a, b) => a.label.localeCompare(b.label));
        }
    }

    checkOrderType(serviceGroup: string = null) {
        if (serviceGroup) {
            this.showOrderType = [this.orderService.SERVICE_GROUP_LABEL_CONVERSION, this.orderService.SERVICE_GROUP_REMS, this.orderService.SERVICE_GROUP_PUBLISHING].includes(serviceGroup);
        }
        else if (this.orderForm.get("step1.serviceGroup").value) {
            this.showOrderType = [this.orderService.SERVICE_GROUP_LABEL_CONVERSION, this.orderService.SERVICE_GROUP_REMS, this.orderService.SERVICE_GROUP_PUBLISHING].includes(this.orderForm.get("step1.serviceGroup").value);
        } else if (this.order?.serviceGroup) {
            this.showOrderType = [this.orderService.SERVICE_GROUP_LABEL_CONVERSION, this.orderService.SERVICE_GROUP_REMS, this.orderService.SERVICE_GROUP_PUBLISHING].includes(this.order.serviceGroup);
        } else {
            this.showOrderType = true;
        }
    }

    // Populate pulldown list of serviceType based on serviceCategory.
    populateServiceGroupLOV(): void {
        // Prevent unwanted nested change events
        if (this.freezeChangeEventsTemporarily) {
            return;
        }
        this.serviceGroupsLOV = [];
        this.filterServiceGroup();
        if (this.order)
        {
          this.orderForm.get("step1.serviceGroup").setValue(this.order.serviceGroup);
        }

    }

    // Hide certain delivery priorities based on certain service catgories and service types
    updateDeliveryPrioritiesVisibility(serviceType: string) {
        // Start by showing all delivery priorities
        this.hasRegularPriority = true;
        this.hasExpeditedPriority = true;
        this.hasEmergencyPriority = true;

        // use ref data to determine all delivery priorities.
            if (serviceType) {

                let currentDeliveryPriority = null;
                if (this.getForm('step1', 'deliveryPriority') && this.getForm('step1', 'deliveryPriority').value) {
                    currentDeliveryPriority = this.getForm('step1', 'deliveryPriority').value;
                }

                // If REGULAR priority is not supported for this service type, hide it
                if (!this.serviceTypeToDeliveryPriorityAssoc
                    .some(x => x.parentTermValueCode === serviceType && x.childTermValueCode === this.orderService.DELIVERY_PRIORITY_REGULAR)) {
                    if (currentDeliveryPriority === this.orderService.DELIVERY_PRIORITY_REGULAR) {
                        // Make sure we don't hide the currently selected delivery priority value
                        this.getForm('step1').patchValue({
                            deliveryPriority: ''
                        });
                    }
                    this.hasRegularPriority = false;
                }

                // If EXPEDITED priority is not supported for this service type, hide it
                if (!this.serviceTypeToDeliveryPriorityAssoc
                    .some(x => x.parentTermValueCode === serviceType && x.childTermValueCode === this.orderService.DELIVERY_PRIORITY_EXPEDITED)) {
                    if (currentDeliveryPriority === this.orderService.DELIVERY_PRIORITY_EXPEDITED) {
                        // Make sure we don't hide the currently selected delivery priority value
                        this.getForm('step1').patchValue({
                            deliveryPriority: ''
                        });
                    }
                    this.hasExpeditedPriority = false;
                }

                // If EMERGENCY priority is not supported for this service type, hide it
                if (!this.serviceTypeToDeliveryPriorityAssoc
                    .some(x => x.parentTermValueCode === serviceType && x.childTermValueCode === this.orderService.DELIVERY_PRIORITY_EMERGENCY)) {
                    if (currentDeliveryPriority === this.orderService.DELIVERY_PRIORITY_EMERGENCY) {
                        // Make sure we don't hide the currently selected delivery priority value
                        this.getForm('step1').patchValue({
                            deliveryPriority: ''
                        });
                    }
                    this.hasEmergencyPriority = false;
                }
            }
    }

    getForm(section: string, field: string = null): UntypedFormGroup {
        return field ? this.orderForm.get(section).get(field) as UntypedFormGroup : this.orderForm.get(section) as UntypedFormGroup;
    }

    resetOrderData() {
        this.order.baselineOrderId = null;
        this.order.deliveryPriority = null;
        this.order.splService = new ILLGenericKeys();
        this.order.actualDeliveryDate = null;
        this.order.confirmedDueDate = null;
        this.order.productName = null;
        this.order.dataCollectionForm = null;
        this.order.additionalInformation = null;
        this.order.acctMgrInstructions = null;
        this.order.sourceDocuments = [];
        this.order.comments = null;
        this.order.commentsChangeUser = null;
        this.order.commentsChangeDate = null;
        this.order.orderComments = [];
        this.order.orderName = null;
        this.order.colOrDrugFactChangeInstruction = null;
        this.order.principalDisplayPanelChangeInstruction = null;
        this.order.LLMetadataChangeInstruction = null;
        this.order.orderDescription = null;
        this.order.serviceCategory = null;
        this.order.serviceGroup = null;
        this.order.country = null;
    }

    // Display detail of a single LL form in a separate tab
    viewLLForm(llRevisionId: number) {
        if (llRevisionId) {
            this.router.navigate([])
                .then(result => {
                    window.open(`/products/ll-form/${llRevisionId}`, '_blank');
                });
        }
    }

    toHtml(input): any {
        return input ? new DOMParser().parseFromString(input, 'text/html').documentElement.textContent : '';
    }

    // Get LL form high leve detail by revision Id
    getLLByRevisionId(llRevisionId: number) {
        if (llRevisionId) {
            this.llService.getLLHighLevlInfoById(llRevisionId.toString()).subscribe(data => {
                if (data && data['data']) {
                  this.llFormGridSelectedRowData = data['data'];
                  this.getBaselineSpls(llRevisionId);
                }
            },
                error => {
                    this.baselineSplLoading = false;
                    this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
                }
            );
        }
    }

    // ------------------------------------------------------------
    // Grids
    // ------------------------------------------------------------
  llFormGridRowSelected(args: RowSelectEventArgs) {
    if (!this.canModifyOrder) {
        this.llFormGrid.clearSelection();
        return;
    }

    const selectedRecords: any[] = this.llFormGrid.getSelectedRecords();
    if (selectedRecords && selectedRecords.length > 0) {
      this.llFormGridSelectedRowData = selectedRecords[0];

      if (args.isInteracted) {
        this.bIsSelectedLLFormActive = true;
        this.getBaselineSpls(selectedRecords[0].llRevisionId);
        this.getForm('step2').markAsDirty();
        this.getForm('step2').markAsTouched();
      } else {
        this.cdRef.detectChanges();
      }
    } else if (args.isInteracted) {
      this.llFormGridSelectedRowData = null;
    }
  }

    llFormGridRowDeselected(args: RowDeselectEventArgs) {
        if (!args.isInteracted) {
            return;
        }

        setTimeout(() => {
            const selectedRecords: any[] = this.llFormGrid ? this.llFormGrid.getSelectedRecords() : [];
            if (selectedRecords && selectedRecords.length > 0) {
                return;
            }

            this.llFormGridSelectedRowData = null;
            this.getForm('step2', 'llFormGrid').markAsDirty();
            this.getBaselineSpls(null);
            this.getForm('step2').markAsDirty();
            this.getForm('step2').markAsTouched();
        });
    }

    llFormGridActionComplete(args: any): void {
        switch (args?.requestType) {
            case 'paging':
            case 'sorting':
            case 'filtering':
            case 'searching':
            case 'refresh':
                this.syncLlFormGridSelection();
                break;
        }
    }

    private syncLlFormGridSelection(): void {
        if (!this.llFormGrid) {
            return;
        }

        setTimeout(() => {
            if (!this.llFormGrid) {
                return;
            }

            const view = (this.llFormGrid.getCurrentViewRecords() as any[]) || [];
            const selectedRevisionId = this.llFormGridSelectedRowData ? this.llFormGridSelectedRowData.llRevisionId : null;

            this.llFormGrid.clearSelection();

            if (!selectedRevisionId) {
                return;
            }

            const rowIndex = view.findIndex((row: any) => row && row.llRevisionId === selectedRevisionId);
            if (rowIndex >= 0) {
                this.llFormGrid.selectRow(rowIndex);
            }
        });
    }

    baselineSplGridRowSelected(args: RowSelectEventArgs) {
        if (!this.canModifyOrder) {
            this.baselineSplGrid.clearSelection();
            return;
        }
        this.baselineSplGridSelectedId = null;
        const selectedRecords: any[] = this.baselineSplGrid.getSelectedRecords();
        if (selectedRecords && selectedRecords.length > 0 && selectedRecords[0].baselineWorkFlowOrderId) {
            this.baselineSplGridSelectedId = selectedRecords[0].baselineWorkFlowOrderId;
        }
        else if (selectedRecords && selectedRecords.length > 0 && !selectedRecords[0].baselineWorkFlowOrderId) {
            this.baselineSplGridSelectedId = selectedRecords[0].orderRevisionId;
        }
        if (args.isInteracted) {
            this.getForm('step2').markAsDirty();
            this.getForm('step2').markAsTouched();
        }
    }

    baselineSplGridRowDeselected(args: RowDeselectEventArgs) {
        this.baselineSplGridSelectedId = null;
        if (args.isInteracted) {
            this.getForm('step2').markAsDirty();
            this.getForm('step2').markAsTouched();
        }
    }

    getServiceTypeCode(): string {
        let code = null;
        if (this.order && this.order.splService) {
            // The preferred method, match by code, but not used as of 6/15/2020
            // Otherwise try to match by label.
            if (this.order.splService.code) {
                code = this.order.splService.code;
            } else if (this.order.splService.label) {
                // Force array to "any" to use newer ".find" method without VS error
                const match = (this.serviceTypesLOV as any).find(x => x.label === this.order.splService.label);
                if (match) {
                    code = match.code;
                }
            }
        }
        return code;
    }

    // Baseline SPL grid is dependent on LL form grid
    // Everytime someone clicks on ll form grid, do a database lookup
    // to get corresponding baseline spls
    getBaselineSpls(llRevisionId: number) {
        if (llRevisionId) {
            this.baselineSplGridData = null;

            this.orderService.getBaselineSplData(llRevisionId).subscribe(data => {
                if (data && data['data'].length > 0) {
                    this.baselineSplLoading = false;
                    this.baselineSplGridData = data['data'];
                    this.baselineSplRecordCount = this.baselineSplGridData.length;
                    // See comments where this field is defined for explanation
                    if (this.order && this.order.baselineWorkFlowOrderId) {
                        if (!this.wasFirstTimeBaselineSplGridSelectionDone) {
                            this.wasFirstTimeBaselineSplGridSelectionDone = true;
                                // Loop through all rows of baseline Spl data to find match, then force grid selected index
                                for (let i = 0; i <= this.baselineSplGridData.length; i++) {
                                    if (this.baselineSplGridData[i].baselineWorkFlowOrderId === this.order.baselineWorkFlowOrderId && this.baselineSplGridData[i].baselineWorkFlowOrderId) {
                                        this.baselineSplGrid.selectedRowIndex = i;
                                        this.baselineSplGridSelectedId = this.baselineSplGridData[i].baselineWorkFlowOrderId;
                                        this.cdRef.detectChanges();
                                        break;
                                    } else if (this.baselineSplGridData[i].orderRevisionId === this.order.baselineWorkFlowOrderId) {
                                        this.baselineSplGrid.selectedRowIndex = i;
                                        this.baselineSplGridSelectedId = this.baselineSplGridData[i].orderRevisionId;
                                        this.cdRef.detectChanges();
                                        break;
                                    }
                                }
                          }
                    }
                }
            },
                error => {
                    this.baselineSplLoading = false;
                    this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
                }
            );
        }
    }

    // Called after *ALL* files or uploaded, or after one file has an error
    // (yeah that seems weird, we have to do lots of "if" statements to sort through that)
    public onActionComplete: EmitType<ActionCompleteEventArgs> = (args: any, type: string) => {
        let atLeastOneGoodUpload = false;
        if (args && args.fileData) {
            for (const myFileData of args.fileData) {
                if ((myFileData as any).statusCode === "2") {
                    // Got at least one good upload
                    atLeastOneGoodUpload = true;
                    break;
                }
            }
        }

        if (atLeastOneGoodUpload && type ==  this.orderService.UPLOAD_FILE_TYPE_DELIVERY) {
            // Successfully uploaded all files. Finished, navigate to next page.
            this.onOrderAdminFileUpload('success');
        }

        if (atLeastOneGoodUpload && type ==  this.orderService.UPLOAD_FILE_TYPE_SOURCE_DOC) {
            // We got the files, stop waiting for them.
            this.postSaveWaitingForSourceDocUpload = false;
            this.postSaveData();
        }
        this.pageState = this.orderService.PAGE_STATE_READY;
    }

    onIngestFilechanged(event: boolean) {
        this.ingestFileValue = event;
    }

    onIngestFileChange(event) {
        this.ingestFileValue = !this.ingestFileValue;
        this.onOrderAdminFileUpload('onIngestFileChange');
        this.getForm('admin').markAsDirty();
        this.getForm('admin').markAsTouched();
    }

    deleteExistingDeliveryFile() {
        // Ask parent component to delete delivery doc when we save
        this.requestDeletionOfExistingDeliveryDoc = true;
        this.onOrderAdminFileUpload('deleteExistingDeliveryDoc');
        this.getForm('admin').markAsDirty();
        this.getForm('admin').markAsTouched();
    }

    undeleteExistingDeliveryFile() {
        // Ask parent component to "never mind" about deleting the delivery doc
        // Haven't save yet, so the deletion was still pending
        this.requestDeletionOfExistingDeliveryDoc = false;
        this.onOrderAdminFileUpload('deleteExistingDeliveryDoc');
        this.getForm('admin').markAsDirty();
        this.getForm('admin').markAsTouched();
    }

    // Add CORS headers - for file uploads
    public addCorsHeaders(args: any) {
        args.currentRequest.setRequestHeader('Access-Control-Allow-Origin', '*');
    }

    tooltipValue(args) {

        let grid;
        let currentGridId;

        let tooltip: TooltipComponent;

        currentGridId = args.event.currentTarget.firstChild.id;

        if (args.target.scrollWidth <= args.target.clientWidth) {
          args.cancel = true;
        } else {

          if (currentGridId) {
            switch (currentGridId) {
              case 'llFormGrid':
                grid = this.llFormGrid;
                tooltip = this.tooltipDC;
                break;
              case 'baselineSplGrid':
                grid = this.baselineSplGrid;
                tooltip = this.tooltipSPL;
                break;
            }

            const rowInfo = grid.getRowInfo(args.target);
            const rowData = rowInfo.rowData;
            tooltip.cssClass = 'customTooltip';
            tooltip.content =  this.toBrackets(rowData[(rowInfo.column as any).field]);
          }
        }
    }

    isDeliveryPrioritySelected(): boolean {
      return this.getForm('step1', 'deliveryPriority').value !== '';
    }

    isLlFormSelected(llRevisionId: number): boolean {
      return this.llFormGridSelectedRowData && this.llFormGridSelectedRowData.llRevisionId === llRevisionId;
    }

    public onCreated() {
        if (this.fileUpload) {
          (this.fileUpload as any).isForm = false;
        }
    }

    public onRemove() {
        this.getForm('step2').markAsDirty();
        this.getForm('step2').markAsTouched();
    }

    countryChanged: EmitType<ChangeEventArgs> = (event: any) => {
        // Prevent unwanted nested change events
        if (this.freezeChangeEventsTemporarily) {
            return;
        }


        if (!this.serviceCategoryToServiceTypeAssoc) {
            return;
        }

       var fieldsNotEmpty = this.getForm('step1', 'serviceGroup').value ? true: false;
       if (fieldsNotEmpty && event.itemData.value !== this.countryChanged) {
            if (confirm(
                `Are you sure you want to change the country? This change will blank out dependent values.`)) {
                const currentCountry = event.itemData.value;
                // reset the whole order form
                this.getForm('step1').reset();
                // Order Owner list and Order Recipient list
                const userGuid = this.storageManagerService.read('userGuid');
                this.populateEmployeeLists();
                if (this.order) {
                    this.getForm('step1').patchValue({
                        country: currentCountry,
                        drpOrderOwners: this.orderService.getUserGuidsFromOrderUsers(
                            this.allEmployeesInCompany, this.order.orderUsers, this.orderService.ORDER_USER_TYPE_ORDER_OWNER),
                        drpOrderRecipients: this.orderService.getUserGuidsFromOrderUsers(
                            this.allEmployeesInCompany, this.order.orderUsers, this.orderService.ORDER_USER_TYPE_ORDER_RECIPIENT)
                    });
                    this.resetOrderData();
                } else {
                    this.getForm('step1').patchValue({
                        country: currentCountry,
                        drpOrderOwners: [userGuid],
                        drpOrderRecipients: []
                    });
                }
            } else {
                setTimeout(() => {
                    this.freezeChangeEventsTemporarily = true;
                    this.getForm('step1').patchValue({ country: this.countrySelected });
                    this.freezeChangeEventsTemporarily = false;
                }, 200);
                return;
            }
        }
        this.countrySelected = event.itemData.value;
        this.filterServiceGroup();
    }

    filterServiceGroup() {
        if (this.countrySelected != 'United States') {
            this.serviceGroupsLOV = this.countryServiceGroup
                .filter((value) => value.parentTermValueName === this.countrySelected)
                .map(x => x.childTermListName)
                .filter((value, index, self) => self.indexOf(value) === index);
        } else {
            this.serviceGroupsLOV = this.serviceCategoryToServiceTypeAssoc
                .map(x => x.parentTermListName)
                .filter((value, index, self) => self.indexOf(value) === index)
                .sort((a, b) => a.localeCompare(b));
        }
    }

    // ------------------------------------------------------------
    // Destroy
    // ------------------------------------------------------------
    ngOnDestroy(): void {
        this.utilService.toggleClientDropdownState(false);
    }

    toBrackets(str) {
        const retVal = str.split('&lt;').join('<')
          .split('&gt;').join('>')
          .split('&amp;').join('&');
         return retVal;
    }
}
